'use client';
import { useRef, useState } from 'react';
import { useToast } from '@practium/ui';
import { useCreateGroup, useUpdateGroup } from '@practium/features/api';
import type { GroupFormData, SystemSubject, SystemAction } from '../types';
import { GroupSchema, GROUP_FIELD_LABELS } from '../schemas/system.schema';
import { DEFAULT_GROUP_FORM, ALL_SUBJECTS, ALL_ACTIONS, ALL_FEATURES } from '../constants/system.constants';

export interface UseGroupFormOptions {
	id?: string;
	initialData?: GroupFormData;
	onSubmit: () => void;
}

export const useGroupForm = ({ id, initialData, onSubmit }: UseGroupFormOptions) => {
	const toast = useToast();
	const { mutateAsync: createGroup } = useCreateGroup();
	const { mutateAsync: updateGroup } = useUpdateGroup();
	const initialForm = initialData ? { ...DEFAULT_GROUP_FORM, ...initialData } : { ...DEFAULT_GROUP_FORM };
	const [form, setForm] = useState<GroupFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [featureStates, setFeatureStates] = useState<Record<string, boolean | null>>(() => {
		const states: Record<string, boolean | null> = {};
		for (const feature of ALL_FEATURES) {
			const hasRead = (initialData?.permissions ?? []).some(
				p => p.subject === feature.key && p.action === 'read'
			);
			states[feature.key] = hasRead ? true : null;
		}
		return states;
	});
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const set = (key: keyof Pick<GroupFormData, 'name'>, value: string) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const hasPermission = (subject: SystemSubject, action: SystemAction): boolean =>
		form.permissions.some(
			p =>
				(p.subject === subject || p.subject === 'all') &&
				(p.action === action || p.action === 'manage')
		);

	const togglePermission = (subject: SystemSubject, action: SystemAction) => {
		setForm(f => {
			const exists = f.permissions.some(p => p.subject === subject && p.action === action);
			if (exists) {
				return { ...f, permissions: f.permissions.filter(p => !(p.subject === subject && p.action === action)) };
			}
			return { ...f, permissions: [...f.permissions, { subject, action }] };
		});
	};

	const allMarked = ALL_SUBJECTS.every(s => ALL_ACTIONS.every(a => hasPermission(s.key, a.key)));

	const toggleAll = () => {
		if (allMarked) {
			setForm(f => ({ ...f, permissions: [] }));
		} else {
			const all = ALL_SUBJECTS.flatMap(s => ALL_ACTIONS.map(a => ({ subject: s.key, action: a.key })));
			setForm(f => ({ ...f, permissions: all }));
		}
	};

	const getFeature = (subject: 'agronomo-support' | 'technical-support'): boolean | null =>
		featureStates[subject] ?? null;

	const setFeature = (subject: 'agronomo-support' | 'technical-support', enabled: boolean) => {
		const current = featureStates[subject];
		const next = current === enabled ? null : enabled;
		setFeatureStates(s => ({ ...s, [subject]: next }));
		setForm(f => {
			const filtered = f.permissions.filter(p => !(p.subject === subject && p.action === 'read'));
			if (next === true) {
				return { ...f, permissions: [...filtered, { subject, action: 'read' as SystemAction }] };
			}
			return { ...f, permissions: filtered };
		});
	};

	const handleSubmit = async () => {
		const result = GroupSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => GROUP_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			if (id) {
				await updateGroup({ groupId: id, name: form.name, permissions: form.permissions });
			} else {
				await createGroup({ name: form.name, permissions: form.permissions });
			}
			toast.success('Grupo salvo com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar grupo');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, togglePermission, hasPermission, allMarked, toggleAll, getFeature, setFeature, handleSubmit, isDirty };
};
